import { MaterialCommunityIcons } from '@expo/vector-icons';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutChangeEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spacing } from '../../styles';
import { Colors } from '../../themes';
import { getMediaTitleForWidth } from '../media/mediaUtils';
import { SlideAction, ViewMode } from './enums';
import { SlideLayoutContextType, SlideLayoutProps } from './interfaces';

const SlideLayoutContext = createContext<SlideLayoutContextType | null>(null);

const SLIDE_LAYOUT_HEADER_HEIGHT = 65;

export const useSlideLayout = () => {
  const context = useContext(SlideLayoutContext);
  if (!context) {
    throw new Error('useSlideLayout must be used within a SlideLayout');
  }
  return context;
};

// Safe version that returns null if used outside of a SlideLayout
export const useSlideLayoutSafe = () => {
  return useContext(SlideLayoutContext);
};

export const SlideLayout: React.FC<SlideLayoutProps> = ({
  leftColumn,
  rightColumn,
  thirdColumn,
  viewMode = ViewMode.SINGLE,
  onViewModeChange,
  transitionKey,
}) => {
  const animationDuration = 500;
  const [searchParams] = useSearchParams();
  const direction = searchParams.get('direction');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentViewMode, setCurrentViewMode] = useState<ViewMode>(viewMode);
  const [columnTitles, setColumnTitles] = useState<{
    left?: string;
    right?: string;
    third?: string;
  }>({});
  const [columnOverlays, setColumnOverlays] = useState<{
    left?: ReactNode;
    right?: ReactNode;
    third?: ReactNode;
  }>({});
  const [leftTitleWidth, setLeftTitleWidth] = useState<number>(0);
  const [rightTitleWidth, setRightTitleWidth] = useState<number>(0);
  const [thirdTitleWidth, setThirdTitleWidth] = useState<number>(0);

  const LEFT_COLUMN_WIDTH_PERCENTAGE = 45;
  const RIGHT_COLUMN_WIDTH_PERCENTAGE = 55;

  // Get current visible columns based on view mode and start index
  const visibleColumns = useMemo(() => {
    if (currentViewMode === ViewMode.SINGLE) {
      return {
        left: leftColumn,
        right: null,
      };
    } else {
      return {
        left: leftColumn,
        right: rightColumn || null,
      };
    }
  }, [leftColumn, rightColumn, currentViewMode]);

  // Animation values for smooth transitions
  const leftColumnTranslateX = useSharedValue(0);
  const rightColumnTranslateX = useSharedValue(0);
  const leftColumnOpacity = useSharedValue(1);
  const rightColumnOpacity = useSharedValue(1);
  const leftColumnWidth = useSharedValue(
    currentViewMode === ViewMode.SINGLE ? 100 : LEFT_COLUMN_WIDTH_PERCENTAGE
  );
  const rightColumnWidth = useSharedValue(
    currentViewMode === ViewMode.DUAL ? RIGHT_COLUMN_WIDTH_PERCENTAGE : 0
  );
  const thirdColumnOpacity = useSharedValue(0);
  const thirdColumnWidth = useSharedValue(0);
  const isThirdPanelVisibleRef = useRef(false);

  // Prevents the prop-sync useEffect from canceling animations triggered internally
  const suppressPropSyncRef = useRef(false);

  const switchToSingleMode = useCallback(() => {
    suppressPropSyncRef.current = true;
    setCurrentViewMode(ViewMode.SINGLE);

    // Fade out right column and expand left
    rightColumnOpacity.value = withTiming(0, { duration: animationDuration });
    rightColumnWidth.value = withTiming(0, { duration: animationDuration });
    leftColumnWidth.value = withTiming(100, { duration: animationDuration });
    
    onViewModeChange?.(ViewMode.SINGLE);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const switchToSingleModeImmediate = useCallback(() => {
    setCurrentViewMode(ViewMode.SINGLE);
    rightColumnOpacity.value = 0;
    leftColumnWidth.value = 100;
    rightColumnWidth.value = 0;
    onViewModeChange?.(ViewMode.SINGLE);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const switchToDualMode = useCallback(() => {
    suppressPropSyncRef.current = true;
    setCurrentViewMode(ViewMode.DUAL);
    leftColumnWidth.value = 100;
    rightColumnWidth.value = 0;

    // Expand right column and shrink left
    leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });
    rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
      duration: animationDuration,
    });
    rightColumnOpacity.value = withTiming(1, { duration: animationDuration });

    onViewModeChange?.(ViewMode.DUAL);
  }, [onViewModeChange, leftColumnWidth, rightColumnWidth, rightColumnOpacity]);

  const slideToNextColumn = useCallback(() => {
    suppressPropSyncRef.current = true;
    // Fade out left column first
    leftColumnOpacity.value = withTiming(0, {
      duration: animationDuration / 2,
    });

    setTimeout(() => {
      // Then shift right column to take left position
      rightColumnTranslateX.value = withTiming(-100, {
        duration: animationDuration / 2,
      });
      rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration / 2,
      });
      suppressPropSyncRef.current = false;
    }, animationDuration / 2);
  }, [leftColumnOpacity, rightColumnTranslateX, rightColumnWidth]);

  const slideToPreviousColumn = useCallback(() => {
    suppressPropSyncRef.current = true;
    // Fade out left column first
    rightColumnOpacity.value = withTiming(0, {
      duration: animationDuration / 2,
    });

    setTimeout(() => {
      // Then shift right column to take left position
      leftColumnTranslateX.value = withTiming(80, {
        duration: animationDuration / 2,
      });
      leftColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, {
        duration: animationDuration / 2,
      });
      suppressPropSyncRef.current = false;
    }, animationDuration / 2);
  }, [rightColumnOpacity, leftColumnTranslateX, leftColumnWidth]);

  const fadeInRightColumns = useCallback(() => {
    rightColumnOpacity.value = 0;
    rightColumnOpacity.value = withTiming(1, {
      duration: animationDuration / 2,
    });
  }, [rightColumnOpacity]);

  const toggleThirdPanel = useCallback(() => {
    if (!thirdColumn) return;

    if (!isThirdPanelVisibleRef.current) {
      // Show third panel
      isThirdPanelVisibleRef.current = true;
      // Phase 1: fade out left column
      leftColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
      // Phase 2: shrink left, resize right to left-width, expand third
      setTimeout(() => {
        leftColumnWidth.value = withTiming(0, { duration: animationDuration / 2 });
        rightColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration / 2 });
        thirdColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
        thirdColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration / 2 });
      }, animationDuration / 2);
    } else {
      // Hide third panel
      isThirdPanelVisibleRef.current = false;
      // Phase 1: fade out third column
      thirdColumnOpacity.value = withTiming(0, { duration: animationDuration / 2 });
      // Phase 2: collapse third, restore right width, expand left back
      setTimeout(() => {
        thirdColumnWidth.value = withTiming(0, { duration: animationDuration / 2 });
        rightColumnWidth.value = withTiming(RIGHT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration / 2 });
        leftColumnWidth.value = withTiming(LEFT_COLUMN_WIDTH_PERCENTAGE, { duration: animationDuration / 2 });
        leftColumnOpacity.value = withTiming(1, { duration: animationDuration / 2 });
      }, animationDuration / 2);
    }
  }, [
    thirdColumn,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnWidth,
    thirdColumnOpacity,
    thirdColumnWidth,
  ]);

  const executeSlideAction = useCallback(
    (action?: SlideAction) => {
      switch (action) {
        case SlideAction.SINGLE:
          switchToSingleMode();
          break;
        case SlideAction.DUAL:
          switchToDualMode();
          break;
        case SlideAction.NEXT:
          slideToNextColumn();
          break;
        case SlideAction.PREV:
          slideToPreviousColumn();
          break;
        default:
          fadeInRightColumns();
          break;
      }
    },
    [
      switchToSingleMode,
      switchToDualMode,
      slideToNextColumn,
      slideToPreviousColumn,
      fadeInRightColumns,
    ]
  );

  // Sync viewMode prop to internal state
  useEffect(() => {
    if (suppressPropSyncRef.current) {
      suppressPropSyncRef.current = false;
      return;
    }
    if (viewMode !== currentViewMode) {
      if (viewMode === ViewMode.DUAL) {
        switchToDualMode();
      } else {
        if (!rightColumn) {
          switchToSingleModeImmediate();
        } else {
          switchToSingleMode();
        }
      }
    }
  }, [
    viewMode,
    currentViewMode,
    rightColumn,
    switchToDualMode,
    switchToSingleMode,
    switchToSingleModeImmediate,
  ]);

  // Keep a stable ref to executeSlideAction to avoid re-firing on every render
  const executeSlideActionRef = useRef(executeSlideAction);
  useEffect(() => {
    executeSlideActionRef.current = executeSlideAction;
  });

  // Handle direction from URL params on load — only re-runs when direction actually changes
  useEffect(() => {
    const action = direction
      ? (direction.toLowerCase() as SlideAction)
      : undefined;

    executeSlideActionRef.current(action);
  }, [direction]);

  // When transitionKey changes in DUAL mode, animate the column transition
  // useLayoutEffect runs before browser paint to avoid flickering on initial frame
  const prevTransitionKey = useRef(transitionKey);
  useLayoutEffect(() => {
    if (transitionKey === prevTransitionKey.current) return;
    if (currentViewMode !== ViewMode.DUAL || !rightColumn) {
      prevTransitionKey.current = transitionKey;
      return;
    }

    const isGoingBack =
      transitionKey === undefined && prevTransitionKey.current !== undefined;
    prevTransitionKey.current = transitionKey;

    rightColumnOpacity.value = 0;

    if (isGoingBack) {
      // Reset widths while columns are hidden (slideToPreviousColumn may have altered them)
      leftColumnWidth.value = LEFT_COLUMN_WIDTH_PERCENTAGE;
      rightColumnWidth.value = RIGHT_COLUMN_WIDTH_PERCENTAGE;
      // Left column slides in from the left (activity list returns from left)
      leftColumnTranslateX.value = -100;
      leftColumnOpacity.value = 0;
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      leftColumnOpacity.value = withTiming(1, { duration: animationDuration });
      rightColumnOpacity.value = withDelay(
        animationDuration,
        withTiming(1, { duration: animationDuration / 2 })
      );
    } else {
      // Forward: left column (Issue) slides in from the right column position
      leftColumnTranslateX.value = 100;
      leftColumnOpacity.value = 0;
      leftColumnTranslateX.value = withTiming(0, {
        duration: animationDuration,
      });
      leftColumnOpacity.value = withTiming(1, { duration: animationDuration });
      rightColumnOpacity.value = withDelay(
        animationDuration,
        withTiming(1, { duration: animationDuration / 2 })
      );
    }
  }, [
    transitionKey,
    currentViewMode,
    rightColumn,
    leftColumnTranslateX,
    leftColumnOpacity,
    leftColumnWidth,
    rightColumnWidth,
    rightColumnOpacity,
  ]);

  const onSlideChange = useCallback(
    (action: SlideAction): Promise<void> => {
      return new Promise((resolve) => {
        executeSlideAction(action);

        setTimeout(() => {
          resolve();
        }, animationDuration);
      });
    },
    [executeSlideAction]
  );

  const handleBackPress = async (column: 'left' | 'right') => {
    const columnData =
      column === 'left' ? visibleColumns.left : visibleColumns.right;

    if (columnData?.backButtonTransition) {
      await onSlideChange(columnData?.backButtonTransition);
    }

    if (columnData?.onBackButtonPress) {
      columnData.onBackButtonPress();
      return;
    }

    if (!columnData?.backButtonTransition) {
      if (currentViewMode === ViewMode.DUAL) {
        // Animate left column expanding to full width before navigating back
        await onSlideChange(SlideAction.SINGLE);
      } else {
        await onSlideChange(SlideAction.PREV);
      }
    }

    navigate(-1);
  };

  const setColumnTitle = useCallback(
    (column: 'left' | 'right' | 'third', title: string | undefined) => {
      setColumnTitles((prev) => ({ ...prev, [column]: title }));
    },
    []
  );

  const setColumnOverlay = useCallback(
    (column: 'left' | 'right' | 'third', overlay: ReactNode | null) => {
      setColumnOverlays((prev) => ({
        ...prev,
        [column]: overlay ?? undefined,
      }));
    },
    []
  );

  const handleTitleLayout = useCallback(
    (column: 'left' | 'right' | 'third', event: LayoutChangeEvent) => {
      const { width } = event.nativeEvent.layout;
      const columnData =
        column === 'left'
          ? visibleColumns.left
          : column === 'right'
          ? visibleColumns.right
          : thirdColumn;
      const currentWidth =
        column === 'left'
          ? leftTitleWidth
          : column === 'right'
          ? rightTitleWidth
          : thirdTitleWidth;
      const setWidth =
        column === 'left'
          ? setLeftTitleWidth
          : column === 'right'
          ? setRightTitleWidth
          : setThirdTitleWidth;

      // Subtract buffer for back button and padding
      const constrainedWidth = Math.max(
        0,
        width - (columnData?.showBackButton ? 300 : 250)
      );

      if (constrainedWidth > 0 && constrainedWidth !== currentWidth) {
        setWidth(constrainedWidth);
      }
    },
    [visibleColumns.left, visibleColumns.right, thirdColumn, leftTitleWidth, rightTitleWidth, thirdTitleWidth]
  );

  const leftColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'left' as const,
      currentViewMode,
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('left', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('left', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [currentViewMode, setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  const rightColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'right' as const,
      currentViewMode,
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('right', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('right', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [currentViewMode, setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  const thirdColumnContextValue: SlideLayoutContextType = useMemo(
    () => ({
      column: 'third' as const,
      currentViewMode,
      setColumnTitle: (title: string | undefined) =>
        setColumnTitle('third', title),
      setColumnOverlay: (overlay: ReactNode | null) =>
        setColumnOverlay('third', overlay),
      onSlideChange,
      toggleThirdPanel,
    }),
    [currentViewMode, setColumnTitle, setColumnOverlay, onSlideChange, toggleThirdPanel]
  );

  const leftColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${leftColumnTranslateX.value}%` }],
    opacity: leftColumnOpacity.value,
    width: `${leftColumnWidth.value}%`,
  }));

  const rightColumnAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: `${rightColumnTranslateX.value}%` }],
    opacity: rightColumnOpacity.value,
    width: `${rightColumnWidth.value}%`,
  }));

  const thirdColumnAnimatedStyle = useAnimatedStyle(() => ({
    opacity: thirdColumnOpacity.value,
    width: `${thirdColumnWidth.value}%`,
    overflow: 'hidden',
  }));

  return (
    <View style={styles.container}>
      <View style={styles.columnsContainer}>
        {/* Left Column */}
        {visibleColumns.left && (
          <SlideLayoutContext.Provider value={leftColumnContextValue}>
            <Animated.View style={[styles.leftColumn, leftColumnAnimatedStyle]}>
              <View style={styles.columnContent}>
                {visibleColumns.left.showHeader !== false && (
                  <View style={styles.header}>
                    <View
                      style={[styles.headerLeft, { flex: 1 }]}
                      onLayout={(event) => handleTitleLayout('left', event)}
                    >
                      {visibleColumns.left.showBackButton && (
                        <Pressable
                          style={styles.backButton}
                          onPress={() => handleBackPress('left')}
                        >
                          <MaterialCommunityIcons
                            name="arrow-left"
                            size={24}
                            color={Colors.black}
                          />
                        </Pressable>
                      )}
                      <Text style={styles.headerTitleText} numberOfLines={1}>
                        {leftTitleWidth > 0
                          ? getMediaTitleForWidth(
                              t(columnTitles.left ?? visibleColumns.left.title),
                              '',
                              leftTitleWidth
                            )
                          : t(columnTitles.left ?? visibleColumns.left.title)}
                      </Text>
                    </View>
                    {visibleColumns.left.headerComponent && (
                      <View style={styles.headerActions}>
                        {visibleColumns.left.headerComponent}
                      </View>
                    )}
                  </View>
                )}
                <View style={styles.sectionContent}>
                  {visibleColumns.left.disableScroll ? (
                    visibleColumns.left.mainComponent
                  ) : (
                    <ScrollView style={styles.scrollContainer}>
                      {visibleColumns.left.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.left && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.left}
                  </View>
                )}
              </View>
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

        {/* Right Column */}
        {currentViewMode === ViewMode.DUAL && (
          <SlideLayoutContext.Provider value={rightColumnContextValue}>
            <Animated.View style={[rightColumnAnimatedStyle]}>
              {visibleColumns.right && (
                <View style={styles.columnContent}>
                  {visibleColumns.right.showHeader !== false && (
                    <View style={styles.header}>
                      <View
                        style={[styles.headerLeft, { flex: 1 }]}
                        onLayout={(event) => handleTitleLayout('right', event)}
                      >
                        {visibleColumns.right.showBackButton && (
                          <Pressable
                            style={styles.backButton}
                            onPress={() => handleBackPress('right')}
                          >
                            <MaterialCommunityIcons
                              name="arrow-left"
                              size={24}
                              color={Colors.black}
                            />
                          </Pressable>
                        )}
                        <Text style={styles.headerTitleText} numberOfLines={1}>
                          {rightTitleWidth > 0
                            ? getMediaTitleForWidth(
                                t(
                                  columnTitles.right ??
                                    visibleColumns.right.title
                                ),
                                '',
                                rightTitleWidth
                              )
                            : t(
                                columnTitles.right ?? visibleColumns.right.title
                              )}
                        </Text>
                      </View>
                      {visibleColumns.right.headerComponent && (
                        <View style={styles.headerActions}>
                          {visibleColumns.right.headerComponent}
                        </View>
                      )}
                    </View>
                  )}
                  <View style={styles.sectionContent}>
                    {visibleColumns.right.disableScroll ? (
                      visibleColumns.right.mainComponent
                    ) : (
                      <ScrollView style={styles.scrollContainer}>
                        {visibleColumns.right.mainComponent}
                      </ScrollView>
                    )}
                  </View>
                  {columnOverlays.right && (
                    <View style={styles.columnOverlay}>
                      {columnOverlays.right}
                    </View>
                  )}
                </View>
              )}
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}

        {/* Third Column (hidden panel) */}
        {thirdColumn && currentViewMode === ViewMode.DUAL && (
          <SlideLayoutContext.Provider value={thirdColumnContextValue}>
            <Animated.View style={[styles.thirdColumn, thirdColumnAnimatedStyle]}>
              <View style={styles.columnContent}>
                {thirdColumn.showHeader !== false && (
                  <View style={styles.header}>
                    <View
                      style={[styles.headerLeft, { flex: 1 }]}
                      onLayout={(event) => handleTitleLayout('third', event)}
                    >
                      {thirdColumn.showBackButton && (
                        <Pressable
                          style={styles.backButton}
                          onPress={() => {
                            toggleThirdPanel();
                            thirdColumn.onBackButtonPress?.();
                          }}
                        >
                          <MaterialCommunityIcons
                            name="arrow-left"
                            size={24}
                            color={Colors.black}
                          />
                        </Pressable>
                      )}
                      <Text style={styles.headerTitleText} numberOfLines={1}>
                        {thirdTitleWidth > 0
                          ? getMediaTitleForWidth(
                              t(columnTitles.third ?? thirdColumn.title),
                              '',
                              thirdTitleWidth
                            )
                          : t(columnTitles.third ?? thirdColumn.title)}
                      </Text>
                    </View>
                    {thirdColumn.headerComponent && (
                      <View style={styles.headerActions}>
                        {thirdColumn.headerComponent}
                      </View>
                    )}
                  </View>
                )}
                <View style={styles.sectionContent}>
                  {thirdColumn.disableScroll ? (
                    thirdColumn.mainComponent
                  ) : (
                    <ScrollView style={styles.scrollContainer}>
                      {thirdColumn.mainComponent}
                    </ScrollView>
                  )}
                </View>
                {columnOverlays.third && (
                  <View style={styles.columnOverlay}>
                    {columnOverlays.third}
                  </View>
                )}
              </View>
            </Animated.View>
          </SlideLayoutContext.Provider>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  columnsContainer: {
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  leftColumn: {
    paddingRight: 16,
  },
  thirdColumn: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  scrollContainer: {
    flex: 1,
  },
  columnContent: {
    flex: 1,
  },
  header: {
    height: SLIDE_LAYOUT_HEADER_HEIGHT,
    minHeight: SLIDE_LAYOUT_HEADER_HEIGHT,
    borderRadius: 4,
    marginBottom: 2,
    backgroundColor: Colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: Spacing.basePadding.base,
    paddingHorizontal: Spacing.basePadding.base / 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitleText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.black,
    marginLeft: 4,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginRight: 4,
  },
  sectionContent: {
    borderRadius: 4,
    backgroundColor: Colors.white,
    flexGrow: 1,
    overflow: 'hidden',
    height: 600,
  },
  columnOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});
